import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, UserPlus } from 'lucide-react'
import { supportTaskApi } from '../services/api'
import { showError } from '../utils/showError'
import {
  PageHeader,
  LoadingSpinner,
  EmptyState,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormField,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '../components/common'

const TaskSettings = () => {
  const navigate = useNavigate()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    receiveEmailOnTaskCreated: true,
    receiveEmailOnTaskAssigned: true,
    receiveEmailOnTaskEdited: true,
    receiveEmailOnTaskCommentAdded: true,
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadMembers()
  }, [])

  const loadMembers = async () => {
    try {
      setLoading(true)
      const response = await supportTaskApi.getMembers()
      setMembers(response.data || [])
    } catch (error) {
      console.error('Error loading members:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddMember = async (e) => {
    e.preventDefault()
    const newErrors = {}
    if (!newMember.name.trim()) newErrors.name = 'Name is required'
    if (!newMember.email.trim()) newErrors.email = 'Email is required'
    if (newMember.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email)) newErrors.email = 'Invalid email'
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    try {
      setSaving(true)
      await supportTaskApi.createMember(newMember)
      setNewMember({
        name: '',
        email: '',
        receiveEmailOnTaskCreated: true,
        receiveEmailOnTaskAssigned: true,
        receiveEmailOnTaskEdited: true,
        receiveEmailOnTaskCommentAdded: true,
      })
      setShowAddForm(false)
      setErrors({})
      await loadMembers()
    } catch (error) {
      console.error('Error creating member:', error)
      showError(error)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (member, field) => {
    try {
      await supportTaskApi.updateMember(member.taskMemberId, { [field]: !member[field] })
      setMembers(prev => prev.map(m =>
        m.taskMemberId === member.taskMemberId ? { ...m, [field]: !m[field] } : m
      ))
    } catch (error) {
      console.error('Error updating member:', error)
      showError(error)
    }
  }

  const handleDelete = async (member) => {
    if (!window.confirm(`Delete member "${member.name}"? Tasks assigned to this member will become unassigned.`)) return
    try {
      await supportTaskApi.deleteMember(member.taskMemberId)
      await loadMembers()
    } catch (error) {
      console.error('Error deleting member:', error)
      showError(error)
    }
  }

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-primary-500' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
      }`} />
    </button>
  )

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Task Settings"
        subtitle="Manage task members and notification preferences"
        action={
          <Button variant="secondary" onClick={() => navigate('/tasks')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Tasks
          </Button>
        }
      />

      {/* Members */}
      <Card>
        <CardHeader
          title="Task Members"
          action={
            <Button
              variant="primary"
              size="small"
              icon={<Plus size={16} />}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              Add Member
            </Button>
          }
        />
        <CardBody>
          {/* Add member form */}
          {showAddForm && (
            <form onSubmit={handleAddMember} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FormField
                  label="Name"
                  name="name"
                  value={newMember.name}
                  onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                  error={errors.name}
                  placeholder="Full name"
                  required
                />
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                  error={errors.email}
                  placeholder="email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Notifications</label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { key: 'receiveEmailOnTaskCreated', label: 'Task Created' },
                    { key: 'receiveEmailOnTaskAssigned', label: 'Task Assigned' },
                    { key: 'receiveEmailOnTaskEdited', label: 'Task Edited' },
                    { key: 'receiveEmailOnTaskCommentAdded', label: 'Comment Added' },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newMember[opt.key]}
                        onChange={(e) => setNewMember(prev => ({ ...prev, [opt.key]: e.target.checked }))}
                        className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="secondary" size="small" onClick={() => { setShowAddForm(false); setErrors({}) }}>
                  Cancel
                </Button>
                <Button variant="primary" size="small" type="submit" loading={saving} icon={<UserPlus size={14} />}>
                  Add Member
                </Button>
              </div>
            </form>
          )}

          {members.length === 0 ? (
            <EmptyState
              icon={UserPlus}
              title="No task members"
              description="Add team members to assign tasks and receive email notifications."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>Name</TableCell>
                    <TableCell header>Email</TableCell>
                    <TableCell header className="text-center">Created</TableCell>
                    <TableCell header className="text-center">Assigned</TableCell>
                    <TableCell header className="text-center">Edited</TableCell>
                    <TableCell header className="text-center">Comment</TableCell>
                    <TableCell header className="text-center">Active</TableCell>
                    <TableCell header></TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map(member => (
                    <TableRow key={member.taskMemberId}>
                      <TableCell>
                        <span className={`text-sm font-medium ${member.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                          {member.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{member.email}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Toggle
                          checked={member.receiveEmailOnTaskCreated}
                          onChange={() => handleToggle(member, 'receiveEmailOnTaskCreated')}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Toggle
                          checked={member.receiveEmailOnTaskAssigned}
                          onChange={() => handleToggle(member, 'receiveEmailOnTaskAssigned')}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Toggle
                          checked={member.receiveEmailOnTaskEdited}
                          onChange={() => handleToggle(member, 'receiveEmailOnTaskEdited')}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Toggle
                          checked={member.receiveEmailOnTaskCommentAdded}
                          onChange={() => handleToggle(member, 'receiveEmailOnTaskCommentAdded')}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Toggle
                          checked={member.isActive}
                          onChange={() => handleToggle(member, 'isActive')}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleDelete(member)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default TaskSettings
